import { Component, Input } from "@angular/core";
import { FormGroup, Validators, FormControl, AbstractControl, ValidationErrors } from "@angular/forms";
import { Tenant, TenantEnvironment } from "../models/tenant";
import { EnvironmentDataService } from "../service/environment-data.service";
import { TenantEnvironmentDataService } from "../service/tenant-environment-data.service";
import { tap, map, catchError } from "rxjs/operators";
import { DialogRef, DialogService } from "@progress/kendo-angular-dialog";
import { Observable, of } from 'rxjs';
import { TenantEnvironmentServiceConfigurationDataService } from '../service/service-configuration-keys-data.service';
import { InlineEditGridComponent } from 'src/app/core/inline-edit-grid.component';

@Component({
  selector: "app-tenant-environments",
  templateUrl: "./tenant-environments.component.html",
  styleUrls: ["./tenant-environments.component.scss"],
})
export class TenantEnvironmentsComponent extends InlineEditGridComponent {
  @Input() public tenant: Tenant;
  public environmentMap: Map<number, string> = new Map();
  public environments: any[];
  constructor(
    private dataService: TenantEnvironmentDataService,
    private environmentDataService: EnvironmentDataService,
    private tenantEnvironmentConfigDataService: TenantEnvironmentServiceConfigurationDataService,
    private dialogService: DialogService
  ) {
    super();
  }

  ngOnInit() {
    this.environmentDataService.get({}, "").subscribe((result) => {
      this.environments = result.data;
      for (const environment of this.environments) {
        this.environmentMap[environment.Id] = environment.Name;
      }
    });
    super.ngOnInit();
  }

  protected refresh() {
    this.view = this.dataService
      .get(this.gridState, `&$filter=TenantId eq ${this.tenant.Id}`)
      .pipe(tap(() => (this.loading = false)));
  }

  public addHandler({ sender }) {
    this.closeEditor(sender);
    this.formGroup = new FormGroup({
      Id: new FormControl(0),
      TenantId: new FormControl(this.tenant.Id),
      EnvironmentId: this.CreateEnvironmentIdControl(null)
    });
    sender.addRow(this.formGroup);
  }

  public editHandler({ sender, rowIndex, dataItem }) {
    this.closeEditor(sender);
    this.formGroup = new FormGroup({
      Id: new FormControl(dataItem.Id),
      TenantId: new FormControl(dataItem.TenantId),
      EnvironmentId: this.CreateEnvironmentIdControl(dataItem.EnvironmentId)
    });
    this.editedRowIndex = rowIndex;
    sender.editRow(rowIndex, this.formGroup);
  }

  public saveHandler({ sender, rowIndex, formGroup, isNew }) {
    const entity: TenantEnvironment = formGroup.value;
    this.loading = true;
    this.refreshObservable(this.dataService.update(entity, isNew));
    sender.closeRow(rowIndex);
  }

  public removeHandler({ dataItem }) {
    const deleteAction = { text: "Delete", primary: true };
    const cancelAction = { text: "Cancel" };

    this.tenantEnvironmentConfigDataService.overrideCountByTenantEnvironment(+dataItem.Id)
    .subscribe(ec => 
      {
        if (+ec['@odata.count'] > 0) {
          let phrase = 'service key override';
          if (+ec['@odata.count'] > 1) {
            phrase = phrase + 's';
          }
          this.dialogService.open({
            title: "Cannot Delete",
            content: `This tenant-environment has ${ec['@odata.count']} ${phrase}`,
            actions: [cancelAction],
          });
          return;
        }

        const dialog: DialogRef = this.dialogService.open({
          title: "Please confirm",
          content: "Are you sure you want to remove this tenant from this environment?",
          actions: [cancelAction, deleteAction],
        });
    
        dialog.result.subscribe((result) => {
          if (result === deleteAction) {
            this.refreshObservable(this.dataService.delete(dataItem.Id));
          }
        });
      });
  }

  private CreateEnvironmentIdControl(environmentId){
    return new FormControl(environmentId ?? 1, {validators: [Validators.required], asyncValidators: [this.UniqueEnvironment.bind(this)]});
  }

  UniqueEnvironment(ctl: AbstractControl) : Observable<ValidationErrors | null> {
    if (ctl.pristine)
      return of(null);

    let tenantIdCtl = ctl.parent.controls['TenantId'];

    return this.dataService.linkExists(+tenantIdCtl.value, +ctl.value)
    .pipe(
      map(
        r => 
        r === true ? { unique: true } : null),
      catchError(() => of(null))
    );
  }   
}
